import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { Link } from "react-router-dom";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { formatDate, timeAgo } from "@/lib/format";
import {
  MagnifyingGlass, ArrowClockwise, SignIn, Buildings, FilePdf, Handshake, ChatCircle,
  ShieldCheck, UploadSimple, UsersThree, Files, Bookmark, ClipboardText, WarningCircle, Info, ArrowRight,
} from "@phosphor-icons/react";

// Icon + colour swatch per action family
const ACTION_STYLE = {
  login: { icon: SignIn, tone: "bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-200" },
  company_view: { icon: Buildings, tone: "bg-emerald-50 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300" },
  document_view: { icon: FilePdf, tone: "bg-blue-50 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300" },
  meeting_request: { icon: Handshake, tone: "bg-purple-50 text-purple-700 dark:bg-purple-900/40 dark:text-purple-300" },
  meeting_update: { icon: Handshake, tone: "bg-purple-100 text-purple-800 dark:bg-purple-900/50 dark:text-purple-200" },
  enquiry: { icon: ChatCircle, tone: "bg-amber-50 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300" },
  enquiry_update: { icon: ChatCircle, tone: "bg-amber-100 text-amber-800 dark:bg-amber-900/50 dark:text-amber-200" },
  nda_accept: { icon: ShieldCheck, tone: "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/50 dark:text-emerald-200" },
  bookmark: { icon: Bookmark, tone: "bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300" },
  investor_register: { icon: UsersThree, tone: "bg-emerald-50 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300" },
  firm_approve: { icon: UsersThree, tone: "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/50 dark:text-emerald-200" },
  firm_reject: { icon: WarningCircle, tone: "bg-red-50 text-red-700 dark:bg-red-900/40 dark:text-red-300" },
  companies_import: { icon: UploadSimple, tone: "bg-indigo-50 text-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-300" },
  disclaimer_publish: { icon: ClipboardText, tone: "bg-amber-100 text-amber-800 dark:bg-amber-900/50 dark:text-amber-200" },
  declaration_accept: { icon: ShieldCheck, tone: "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/50 dark:text-emerald-200" },
  user_classify: { icon: UsersThree, tone: "bg-purple-50 text-purple-700 dark:bg-purple-900/40 dark:text-purple-300" },
  user_approval: { icon: UsersThree, tone: "bg-blue-50 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300" },
  company_compliance_update: { icon: Buildings, tone: "bg-amber-50 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300" },
  company_duplicate: { icon: Buildings, tone: "bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-200" },
  compliance_doc_upload: { icon: Files, tone: "bg-emerald-50 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300" },
  compliance_doc_delete: { icon: Files, tone: "bg-red-50 text-red-700 dark:bg-red-900/40 dark:text-red-300" },
};
const DEFAULT_STYLE = { icon: Info, tone: "bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-200" };

const ROLE_TONE = {
  super_admin: "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300",
  investment_team: "bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300",
  investor: "bg-purple-100 text-purple-800 dark:bg-purple-900/40 dark:text-purple-300",
  client: "bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300",
};

export default function AdminAudit() {
  const [logs, setLogs] = useState([]);
  const [actionTypes, setActionTypes] = useState([]);
  const [q, setQ] = useState("");
  const [action, setAction] = useState("all");
  const [limit, setLimit] = useState(200);
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const params = { limit };
      if (action && action !== "all") params.action = action;
      if (q.trim()) params.q = q.trim();
      const r = await api.get("/admin/audit-logs", { params });
      setLogs(r.data);
    } finally { setLoading(false); }
  };

  useEffect(() => {
    api.get("/admin/audit-logs/actions").then((r) => setActionTypes(r.data)).catch(() => setActionTypes([]));
  }, []);
  useEffect(() => { load(); }, [action, limit]);

  const styleFor = (a) => ACTION_STYLE[a] || DEFAULT_STYLE;

  const counts = useMemo(() => {
    const map = {};
    logs.forEach((l) => { map[l.action] = (map[l.action] || 0) + 1; });
    return map;
  }, [logs]);

  const topActions = useMemo(() => Object.entries(counts).sort((a, b) => b[1] - a[1]).slice(0, 5), [counts]);

  return (
    <div className="px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-6 flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-[11px] uppercase tracking-[0.2em] font-bold text-muted-foreground mb-2">Admin · Security</div>
          <h1 className="font-display text-4xl font-bold tracking-tight">Audit log</h1>
          <p className="mt-2 text-muted-foreground text-sm max-w-2xl">
            Every user action on the platform, in reverse-chronological order. Enriched with the actor, the resource that
            was touched and the meaningful metadata (status transitions, disclaimer version, imported counts, etc.).
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={load} data-testid="audit-refresh-btn">
            <ArrowClockwise size={13} className="mr-1" /> Refresh
          </Button>
        </div>
      </div>

      {/* Top-actions summary chips */}
      {topActions.length > 0 && (
        <div className="mb-6 flex flex-wrap gap-2" data-testid="audit-summary-chips">
          {topActions.map(([a, count]) => {
            const st = styleFor(a);
            const Icon = st.icon;
            const label = logs.find((l) => l.action === a)?.action_label || a.replace(/_/g, " ");
            return (
              <button
                key={a}
                onClick={() => setAction(a)}
                className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-semibold ${st.tone} hover:brightness-105 transition-all`}
                data-testid={`chip-${a}`}
              >
                <Icon size={12} weight="fill" /> {label} · {count}
              </button>
            );
          })}
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <form
          onSubmit={(e) => { e.preventDefault(); load(); }}
          className="relative flex-1 max-w-md min-w-[220px]"
        >
          <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search by user email, action or ID…"
            className="pl-9"
            data-testid="audit-search-input"
          />
        </form>
        <Select value={action} onValueChange={setAction}>
          <SelectTrigger className="w-[240px]" data-testid="audit-action-filter">
            <SelectValue placeholder="All actions" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All actions ({logs.length})</SelectItem>
            {actionTypes.map((a) => (
              <SelectItem key={a} value={a}>{a.replace(/_/g, " ")}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={String(limit)} onValueChange={(v) => setLimit(Number(v))}>
          <SelectTrigger className="w-[130px]" data-testid="audit-limit-select">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="50">Last 50</SelectItem>
            <SelectItem value="200">Last 200</SelectItem>
            <SelectItem value="500">Last 500</SelectItem>
            <SelectItem value="1000">Last 1000</SelectItem>
          </SelectContent>
        </Select>
        {(q || action !== "all") && (
          <Button variant="ghost" size="sm" onClick={() => { setQ(""); setAction("all"); }} data-testid="audit-clear-btn">
            Clear filters
          </Button>
        )}
      </div>

      <div className="rounded-lg border border-border bg-card overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-secondary/50 hover:bg-secondary/50">
              <TableHead className="w-[180px]">When</TableHead>
              <TableHead className="w-[240px]">User</TableHead>
              <TableHead className="w-[220px]">Action</TableHead>
              <TableHead>Target</TableHead>
              <TableHead className="w-[140px]">IP</TableHead>
              <TableHead className="w-[70px] text-right"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading && (
              <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground py-8">Loading audit log…</TableCell></TableRow>
            )}
            {!loading && logs.length === 0 && (
              <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground py-8">No audit events match your filters.</TableCell></TableRow>
            )}
            {!loading && logs.map((l) => {
              const st = styleFor(l.action);
              const Icon = st.icon;
              const target = l.target || {};
              const targetHref =
                target.kind === "company" && target.company_id ? `/companies/${target.company_id}` :
                target.kind === "document" && target.company_id ? `/companies/${target.company_id}` :
                target.kind === "user" && l.user_id ? `/admin/users/${target.user_id || l.user_id}/audit` :
                null;
              return (
                <TableRow key={l.id} data-testid={`audit-row-${l.id}`}>
                  <TableCell className="text-xs text-muted-foreground">
                    <div className="num-display font-medium text-foreground">{timeAgo(l.created_at)}</div>
                    <div className="num-display text-[10px]">{formatDate(l.created_at)} · {new Date(l.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</div>
                  </TableCell>
                  <TableCell>
                    <div className="text-sm font-medium truncate">{l.user_name || l.user_email?.split("@")[0]}</div>
                    <div className="text-xs text-muted-foreground truncate">{l.user_email}</div>
                    {l.user_role && (
                      <span className={`inline-block mt-1 text-[9px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded ${ROLE_TONE[l.user_role] || "bg-slate-100 text-slate-700"}`}>
                        {l.user_role.replace("_", " ")}
                      </span>
                    )}
                  </TableCell>
                  <TableCell>
                    <div className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-md text-xs font-semibold ${st.tone}`}>
                      <Icon size={12} weight="fill" /> {l.action_label}
                    </div>
                    {l.detail && (
                      <div className="text-[11px] text-muted-foreground mt-1 flex items-center gap-1">
                        <ArrowRight size={10} /> {l.detail}
                      </div>
                    )}
                  </TableCell>
                  <TableCell>
                    {target.primary && target.primary !== "—" ? (
                      <div className="min-w-0">
                        <div className="text-sm font-medium truncate">
                          {targetHref ? (
                            <Link to={targetHref} className="hover:underline decoration-emerald-500 decoration-2 underline-offset-2">
                              {target.primary}
                            </Link>
                          ) : target.primary}
                        </div>
                        {target.subtitle && (
                          <div className="text-xs text-muted-foreground truncate">{target.subtitle}</div>
                        )}
                      </div>
                    ) : (
                      <span className="text-xs text-muted-foreground italic">—</span>
                    )}
                  </TableCell>
                  <TableCell className="text-xs num-display text-muted-foreground">{l.ip || "—"}</TableCell>
                  <TableCell className="text-right">
                    <Button size="sm" variant="ghost" className="h-7 px-2" onClick={() => setDetail(l)} data-testid={`audit-detail-${l.id}`} title="View full metadata">
                      <Info size={13} />
                    </Button>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>

      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="max-w-lg" data-testid="audit-detail-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-xl font-bold flex items-center gap-2">
              {detail && (() => {
                const st = styleFor(detail.action);
                const Icon = st.icon;
                return <><Icon size={18} className="text-emerald-600" weight="fill" /> {detail.action_label}</>;
              })()}
            </DialogTitle>
          </DialogHeader>
          {detail && (
            <div className="space-y-3 text-sm">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">When</div>
                  <div className="num-display font-medium">{formatDate(detail.created_at)}</div>
                  <div className="text-xs text-muted-foreground num-display">{new Date(detail.created_at).toLocaleString()}</div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">IP</div>
                  <div className="num-display font-medium">{detail.ip || "—"}</div>
                </div>
                <div className="col-span-2">
                  <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">Actor</div>
                  <div className="font-medium">{detail.user_name || detail.user_email}</div>
                  <div className="text-xs text-muted-foreground">{detail.user_email}</div>
                </div>
                <div className="col-span-2">
                  <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">Target</div>
                  <div className="font-medium">{detail.target?.primary || "—"}</div>
                  {detail.target?.subtitle && <div className="text-xs text-muted-foreground">{detail.target.subtitle}</div>}
                </div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">Raw metadata</div>
                <pre className="rounded-lg border border-border bg-secondary/40 p-3 text-[11px] leading-relaxed overflow-x-auto font-mono">
{JSON.stringify(detail.metadata || {}, null, 2)}
                </pre>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
